import { useState, useEffect, useRef } from "react";
import styled from "styled-components";

// Styled Components (same as Global Title Builder)
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 900px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const TitleInputContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 80px;
  border: 1px solid ${(props) => (props.isDragging ? "#3b82f6" : "#d1d5db")};
  border-radius: 8px;
  background: ${(props) => (props.isDragging ? "#ffffff" : "transparent")};
  padding: 0.75rem;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  cursor: text;

  &:hover,
  &:focus-within {
    background: #ffffff;
    border-color: ${(props) => (props.isDragging ? "#3b82f6" : "#9ca3af")};
  }
`;

const TitleInput = styled.div`
  width: 100%;
  min-height: auto;
  font-size: 1rem;
  color: #1f2937;
  line-height: 2;
  outline: none;
  border: none;
  background: transparent;
  font-family: inherit;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 0.25rem 0;

  &[contenteditable="true"]:empty:before {
    content: attr(data-placeholder);
    color: #9ca3af;
    font-style: italic;
    pointer-events: none;
  }

  .field-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    background: #dbeafe;
    border-radius: 999px;
    color: #1d4ed8;
    font-weight: 600;
    font-size: 0.875rem;
    margin: 0 0.125rem;
    user-select: none;
    cursor: default;
    contenteditable: false;

    .field-badge-remove {
      background: transparent;
      border: none;
      color: #ef4444;
      cursor: pointer;
      padding: 0;
      font-size: 1rem;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      transition: all 0.2s ease;
      margin-left: 0.25rem;
      border-radius: 50%;

      &:hover {
        background: rgba(239, 68, 68, 0.1);
        transform: scale(1.2);
        color: #dc2626;
      }
    }
  }
`;

const ColumnTitle = styled.h4`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.625rem;
  border-bottom: 2px solid #e5e7eb;
  letter-spacing: -0.01em;
`;

const AvailableFieldsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FieldItem = styled.div`
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: move;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  user-select: none;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }

  &.dragging {
    opacity: 0.5;
  }
`;

const PreviewBox = styled.div`
  background: #f2f2f2;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 8px;
  padding: 1.5rem;
  min-height: 80px;
  color: #111827;
  font-size: 1rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  cursor: not-allowed;

  .field-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    background: #dbeafe;
    border-radius: 999px;
    color: #1d4ed8;
    font-weight: 600;
    font-size: 0.875rem;
    margin: 0 0.125rem;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-width: auto;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &.primary {
    background: #3b82f6;
    color: white;
    border: 1px solid #3b82f6;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &:hover:not(:disabled) {
      background: #2563eb;
      border-color: #2563eb;
    }
  }

  &.secondary {
    background: white;
    color: #374151;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  }

  &.small {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    min-width: 80px;
  }
`;

const ErrorMessage = styled.p`
  color: #ef4444;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
`;

const SuccessMessage = styled.p`
  color: #059669;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
  padding: 0.75rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
`;

function BatchTitleBuilder({
  isOpen,
  onClose,
  availableFields,
  configuration,
  onSave,
}) {
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isDraggingOverInput, setIsDraggingOverInput] = useState(false);
  const [draggedField, setDraggedField] = useState(null);
  const [isModalReady, setIsModalReady] = useState(false);
  const titleInputRef = useRef(null);

  // Example values for preview
  const getExampleValue = (fieldName) => {
    const examples = {
      "Card Name": "Pikachu",
      Type: "Electric",
      Manufacturer: "Pokemon",
      Set: "Base Set",
      "Card Number": "25",
      "Year Manufactured": "1999",
    };
    return examples[fieldName] || fieldName;
  };

  // Rebuild contentEditable from configuration array
  const rebuildFromConfiguration = (config) => {
    if (!titleInputRef.current || !Array.isArray(config)) return;

    // Clear existing content
    titleInputRef.current.innerHTML = "";

    // Rebuild content from configuration
    config.forEach((item) => {
      if (item.type === "string") {
        const textNode = document.createTextNode(item.value || "");
        titleInputRef.current.appendChild(textNode);
      } else if (item.type === "field-name") {
        const fieldBadge = document.createElement("span");
        fieldBadge.className = "field-badge";
        fieldBadge.setAttribute("contenteditable", "false");
        fieldBadge.setAttribute("data-field", item.value || "");

        const fieldNameSpan = document.createElement("span");
        fieldNameSpan.textContent = item.value || "";
        fieldBadge.appendChild(fieldNameSpan);

        const removeButton = document.createElement("button");
        removeButton.className = "field-badge-remove";
        removeButton.textContent = "×";
        removeButton.setAttribute("contenteditable", "false");
        removeButton.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          removeFieldBadge(fieldBadge);
        };
        fieldBadge.appendChild(removeButton);

        titleInputRef.current.appendChild(fieldBadge);
      }
    });

    updatePreview();
  };

  // Remove field badge
  const removeFieldBadge = (badgeElement) => {
    if (!badgeElement || !titleInputRef.current) return;

    const textNode = document.createTextNode("");
    badgeElement.parentNode.replaceChild(textNode, badgeElement);

    const selection = window.getSelection();
    const range = document.createRange();
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    updatePreview();
  };

  // Insert field at cursor position
  const insertFieldAtCursor = (fieldName) => {
    if (!titleInputRef.current) return;

    const selection = window.getSelection();
    let range;

    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(titleInputRef.current);
      range.collapse(false);
    }

    const fieldBadge = document.createElement("span");
    fieldBadge.className = "field-badge";
    fieldBadge.setAttribute("contenteditable", "false");
    fieldBadge.setAttribute("data-field", fieldName);

    const fieldNameSpan = document.createElement("span");
    fieldNameSpan.textContent = fieldName;
    fieldBadge.appendChild(fieldNameSpan);

    const removeButton = document.createElement("button");
    removeButton.className = "field-badge-remove";
    removeButton.textContent = "×";
    removeButton.setAttribute("contenteditable", "false");
    removeButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      removeFieldBadge(fieldBadge);
    };
    fieldBadge.appendChild(removeButton);

    range.deleteContents();
    range.insertNode(fieldBadge);

    range.setStartAfter(fieldBadge);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    updatePreview();
  };

  // Generate preview HTML
  const generatePreview = () => {
    if (!titleInputRef.current) {
      return "Your title preview will appear here...";
    }

    const config = formatConfiguration();
    if (config.length === 0) {
      return "Your title preview will appear here...";
    }

    let previewText = "";
    config.forEach((item) => {
      if (item.type === "string") {
        previewText += item.value;
      } else if (item.type === "field-name") {
        const exampleValue = getExampleValue(item.value);
        previewText += exampleValue;
      }
    });

    const escapedText = previewText
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

    return escapedText || "Your title preview will appear here...";
  };

  // Update preview
  const updatePreview = () => {
    if (!titleInputRef.current) {
      setPreview("");
      return;
    }

    setPreview(generatePreview());
  };

  // Format configuration for parent component
  const formatConfiguration = () => {
    if (!titleInputRef.current) return [];

    const config = [];

    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text) {
          config.push({
            type: "string",
            value: text,
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.classList && node.classList.contains("field-badge")) {
          const fieldName = node.getAttribute("data-field");
          if (fieldName) {
            config.push({
              type: "field-name",
              value: fieldName,
            });
          }
        } else {
          for (let child = node.firstChild; child; child = child.nextSibling) {
            processNode(child);
          }
        }
      }
    };

    for (
      let child = titleInputRef.current.firstChild;
      child;
      child = child.nextSibling
    ) {
      processNode(child);
    }

    return config;
  };

  // Reset modal readiness when it closes
  useEffect(() => {
    if (!isOpen) {
      setIsModalReady(false);
    }
  }, [isOpen]);

  // Load configuration when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setError("");
    setSuccess("");
    setIsModalReady(false);

    // Convert configuration to format for contentEditable
    if (
      configuration &&
      Array.isArray(configuration) &&
      configuration.length > 0
    ) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          rebuildFromConfiguration(configuration);
          // Mark as ready after a small delay to ensure all handlers are attached
          setTimeout(() => {
            setIsModalReady(true);
          }, 50);
        }, 100);
      });
    } else {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (titleInputRef.current) {
            titleInputRef.current.innerHTML = "";
          }
          setPreview("");
          // Mark as ready even with empty configuration
          setTimeout(() => {
            setIsModalReady(true);
          }, 50);
        }, 100);
      });
    }
  }, [isOpen, configuration]);

  // Auto-focus input field when modal is ready
  useEffect(() => {
    if (isOpen && isModalReady && titleInputRef.current) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (titleInputRef.current) {
            titleInputRef.current.focus();
            // Place cursor at the end of content if there's any
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(titleInputRef.current);
            range.collapse(false); // Collapse to end
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, 100);
      });
    }
  }, [isOpen, isModalReady]);

  // Handle input
  const handleInput = () => {
    updatePreview();
  };

  // Handle drag over
  const handleDragOver = (e) => {
    if (!isModalReady) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDraggingOverInput(true);
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    const relatedTarget = e.relatedTarget;
    const currentTarget = e.currentTarget;

    if (relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }

    setIsDraggingOverInput(false);
  };

  // Handle drop
  const handleDrop = (e) => {
    if (!isModalReady) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverInput(false);

    const field = e.dataTransfer.getData("text/plain");
    if (field) {
      insertFieldAtCursor(field);
    }
  };

  // Handle container drag over (prevent drop on container, only allow on input)
  const handleContainerDragOver = (e) => {
    if (!isModalReady) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
      return;
    }
    // Check if the drop target is the TitleInput or its children
    const isOverInput =
      titleInputRef.current &&
      (titleInputRef.current === e.target ||
        titleInputRef.current.contains(e.target));

    // Only allow drop if over the input, otherwise prevent
    if (!isOverInput) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
    }
  };

  const handleContainerDrop = (e) => {
    if (!isModalReady) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // Check if the drop target is the TitleInput or its children
    const isOverInput =
      titleInputRef.current &&
      (titleInputRef.current === e.target ||
        titleInputRef.current.contains(e.target));

    // Only allow drop if over the input, otherwise prevent
    if (!isOverInput) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    const selection = window.getSelection();
    if (selection.rangeCount) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      updatePreview();
    }
  };

  // Clear all
  const handleClear = () => {
    if (titleInputRef.current) {
      titleInputRef.current.innerHTML = "";
      setPreview("");
      setError("");
      setSuccess("");
    }
  };

  // Handle save
  const handleSave = () => {
    const config = formatConfiguration();

    if (config.length === 0) {
      setError("Please add at least one field to save the configuration.");
      setSuccess("");
      return;
    }

    setError("");

    // Call parent's onSave callback with the configuration
    if (onSave) {
      onSave(config);
    }

    // Close the modal after saving
    handleClose();
  };

  // Handle close
  const handleClose = () => {
    setError("");
    setSuccess("");
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay
      onClick={handleClose}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "none";
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <ModalContainer
        onClick={(e) => e.stopPropagation()}
        onDragOver={(e) => {
          // Check if the drop target is the TitleInput or its children
          const isOverInput =
            titleInputRef.current &&
            (titleInputRef.current === e.target ||
              titleInputRef.current.contains(e.target));

          // Only allow drop if over the input, otherwise prevent
          if (!isOverInput) {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "none";
          }
        }}
        onDrop={(e) => {
          // Check if the drop target is the TitleInput or its children
          const isOverInput =
            titleInputRef.current &&
            (titleInputRef.current === e.target ||
              titleInputRef.current.contains(e.target));

          // Only allow drop if over the input, otherwise prevent
          if (!isOverInput) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <ModalHeader>
          <ModalTitle>Batch Title Builder</ModalTitle>
        </ModalHeader>
        <ModalContent
          onDragOver={(e) => {
            // Prevent drops on modal content area
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "none";
          }}
          onDrop={(e) => {
            // Prevent drops on modal content area
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div>
            <Label
              style={{
                marginBottom: "0.75rem",
                display: "block",
                fontWeight: 600,
              }}
            >
              Title Input
            </Label>
            <TitleInputContainer
              isDragging={isDraggingOverInput && isModalReady}
              onDragOver={handleContainerDragOver}
              onDrop={handleContainerDrop}
              style={{
                opacity: isModalReady ? 1 : 0.7,
                pointerEvents: isModalReady ? "auto" : "none",
              }}
            >
              <TitleInput
                ref={titleInputRef}
                contentEditable={isModalReady}
                onInput={handleInput}
                onPaste={handlePaste}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                data-placeholder="Type your title here or drag fields from below..."
                suppressContentEditableWarning
                style={{
                  pointerEvents: isModalReady ? "auto" : "none",
                }}
              />
            </TitleInputContainer>
          </div>

          <div
            onDragOver={(e) => {
              // Prevent drops on available fields section
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = "none";
            }}
            onDrop={(e) => {
              // Prevent drops on available fields section
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <ColumnTitle>Available Fields</ColumnTitle>
            <AvailableFieldsList>
              {availableFields.map((field) => (
                <FieldItem
                  key={field}
                  draggable={isModalReady}
                  onDragStart={(e) => {
                    if (!isModalReady) {
                      e.preventDefault();
                      return;
                    }
                    setDraggedField(field);
                    e.dataTransfer.setData("text/plain", field);
                    e.dataTransfer.effectAllowed = "copy";
                    if (e.target) {
                      e.target.style.opacity = "0.5";
                    }
                  }}
                  onDragEnd={(e) => {
                    setDraggedField(null);
                    if (e.target) {
                      e.target.style.opacity = "1";
                    }
                  }}
                  className={draggedField === field ? "dragging" : ""}
                  style={{
                    opacity: isModalReady ? 1 : 0.5,
                    cursor: isModalReady ? "move" : "not-allowed",
                  }}
                >
                  {field}
                </FieldItem>
              ))}
            </AvailableFieldsList>
          </div>

          <div
            onDragOver={(e) => {
              // Prevent drops on preview section
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = "none";
            }}
            onDrop={(e) => {
              // Prevent drops on preview section
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <ColumnTitle>Preview</ColumnTitle>
            <PreviewBox
              dangerouslySetInnerHTML={{
                __html: preview || "Your title preview will appear here...",
              }}
            />
          </div>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
        </ModalContent>
        <ModalFooter>
          <Button onClick={handleClear} className="secondary small">
            Clear All
          </Button>
          <Button
            onClick={handleSave}
            className="primary small"
            disabled={formatConfiguration().length === 0}
          >
            Save Configuration
          </Button>
          <Button onClick={handleClose} className="secondary small">
            Close
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
}

export default BatchTitleBuilder;
